<template>
  <div class="custom-functions-view">
    <div class="row">
      <div class="col-md-12 grid-margin">
        <div class="row">
          <div class="col-8 mb-0 mb-xl-0">
            <h4 class="font-weight-bold">
              {{ isEdit ? `Edit Custom Function - ${functionData.name}` : 'Create Custom Function' }}
            </h4>
          </div>
          <div class="col-4">
            <router-link class="btn btn-primary btn-icon-text text-white float-right" to="/custom-functions">
              <i class="fa fa-arrow-left mr-2"></i>
              <span class="h4 text-white">Back to List</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div :class="['custom-functions-view__editor', { 'custom-functions-view__editor--expanded': isConsoleCollapsed }]">
        <div class="custom-functions-view__content">
          

          <ul class="nav nav-tabs menu-form" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" id="editor-tab" data-bs-toggle="tab" href="#editorTab" role="tab"
                aria-controls="editor" aria-selected="true">Editor</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="details-tab" data-bs-toggle="tab" href="#detailsTab" role="tab"
                aria-controls="details" aria-selected="false">Details</a>
            </li>
          </ul>

          <div class="tab-content menu-form">
            <div class="tab-pane fade show active" id="editorTab" role="tabpanel" aria-labelledby="editor-tab">
              <div class="custom-functions-view__templates mb-3">
            <label class="form-group__label">Example Templates</label>
            <select class="form-control" v-model="selectedTemplate" @change="handleTemplateChange">
              <option value="">Select a template</option>
              <option v-for="template in templates" :key="template.uuid" :value="template">
                {{ template.actionName }}
              </option>
            </select>
          </div>
              <custom-function-editor
                :code="functionData.code"
                :session-values="functionData.sessionValues"
                :action-results="functionData.actionResults"
                @save="handleSave"
                @test="handleTest"
              />
            </div>
            <div class="tab-pane fade" id="detailsTab" role="tabpanel" aria-labelledby="details-tab">
              <div class="form-group">
                <label class="form-group__label">Function Name</label>
                <input type="text" class="form-control" v-model="functionData.name" />
              </div>
              <div class="form-group">
                <label class="form-group__label">Description</label>
                <textarea class="form-control" v-model="functionData.description" rows="2"></textarea>
              </div>
              <div class="custom-functions-view__actions">
                <button type="button" class="btn btn-outline-primary btn-lg" @click="handleSaveDetails">Save Details</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="custom-functions-view__console">
        <terminal-console
          :logs="consoleLogs"
          @clear="clearConsole"
          @log="addConsoleLog"
          @collapse="handleConsoleCollapse"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CustomFunctionEditor from '@/components/customFunctionEditor.vue';
import TerminalConsole from '@/components/terminalConsole.vue';
import { useToast } from "vue-toastification";
import api from "@/shared/menuApi";

export default {
  name: 'CustomFunctionsView',
  components: {
    CustomFunctionEditor,
    TerminalConsole
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      functionData: {
        name: '',
        description: '',
        code: '',
        sessionValues: JSON.stringify({
          mobile: "",
          inputs: {
            exampleInputValues: "test value",
            amount: "999",
            fullName: "John Doe"
          }
        }, null, 2),
        actionResults: JSON.stringify({
          restApiActionResults: { status: true, message: "Example restapi result" }
        }, null, 2)
      },
      consoleLogs: [],
      isConsoleCollapsed: false,
      templates: [],
      selectedTemplate: ''
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    }
  },
  methods: {
    handleConsoleCollapse(collapsed) {
      this.isConsoleCollapsed = collapsed;
    },
    clearConsole() {
      this.consoleLogs = [];
    },
    addConsoleLog(log) {
      if (typeof log === 'string') {
        log = { message: log, type: 'info' };
      }
      const timestamp = new Date().toLocaleTimeString();
      this.consoleLogs.push({ ...log, timestamp });
    },
    async loadFunction() {
      if (this.isEdit) {
        try {
          const response = await api.doGet(`/api/custom-action/${this.$route.params.id}`);
          
          if (response.status === "00" && response.data) {
            this.functionData = {
              ...this.functionData,
              name: response.data.actionName,
              description: response.data.description,
              code: response.data.code
            };
            this.addConsoleLog({ message: 'Function loaded successfully', type: 'success' });
          } else {
            throw new Error(response.message || "Failed to load function");
          }
        } catch (error) {
          this.toast.error(error.message || "Failed to load function");
          this.addConsoleLog({ message: `Error: ${error.message}`, type: 'error' });
          this.$router.push('/custom-functions');
        }
      }
    },
    async handleSave(data) {
      try {
        const payload = {
          uuid: this.$route.params.id,
          code: data.code
        };

        if (this.isEdit) {
          const response = await api.doPost('/api/custom-action/update-code', payload);
          
          if (response.status === "00") {
            this.toast.success(response.message || "Function code updated successfully");
            this.addConsoleLog({ message: response.message || 'Function code updated successfully', type: 'success' });
          } else {
            throw new Error(response.message || "Failed to update function code");
          }
        }
      } catch (error) {
        this.toast.error(error.message || "Failed to save function");
        this.addConsoleLog({ message: `Error: ${error.message}`, type: 'error' });
      }
    },
    async handleSaveDetails() {
      try {
        const payload = {
          uuid: this.$route.params.id,
          actionName: this.functionData.name,
          description: this.functionData.description
        };

        const response = await api.doPost('/api/custom-action/update-name', payload);
        
        if (response.status === "00") {
          this.toast.success(response.message || "Function details updated successfully");
          this.addConsoleLog({ message: response.message || 'Function details updated successfully', type: 'success' });
        } else {
          throw new Error(response.message || "Failed to update function details");
        }
      } catch (error) {
        this.toast.error(error.message || "Failed to save function details");
        this.addConsoleLog({ message: `Error: ${error.message}`, type: 'error' });
      }
    },
    async handleTest(data) {
      try {
        const payload = {
          sessionValues: data.sessionValues,
          actionResults: data.actionResults,
          code: data.code
        };

        const response = await api.doPost('/api/custom-action/test', payload);
        
        if (response.status === "00") {
          this.toast.success(response.message || "Function tested successfully");
          this.addConsoleLog({ message: 'Function test started', type: 'info' });
          
          // Log input data
          this.addConsoleLog({ message: 'Input:', type: 'info' });
          this.addConsoleLog({ message: JSON.stringify(data.sessionValues, null, 2), type: 'data' });
          this.addConsoleLog({ message: 'Action Results:', type: 'info' });
          this.addConsoleLog({ message: JSON.stringify(data.actionResults, null, 2), type: 'data' });
          
          // Log console messages from the test execution
          if (response.data.consoleMessages && response.data.consoleMessages.length > 0) {
            this.addConsoleLog({ message: 'Console Output:', type: 'info' });
            response.data.consoleMessages.forEach(msg => {
              this.addConsoleLog({ 
                message: msg.message, 
                type: msg.type || 'log' // Default to 'log' if type is not specified
              });
            });
          }
          
          // Log the final output
          this.addConsoleLog({ message: 'Test Result:', type: 'info' });
          this.addConsoleLog({ 
            message: typeof response.data.output === 'string' 
              ? response.data.output 
              : JSON.stringify(response.data.output, null, 2), 
            type: 'data' 
          });
        } else {
          throw new Error(response.message || "Failed to test function");
        }
      } catch (error) {
        this.toast.error(error.message || "Failed to test function");
        this.addConsoleLog({ message: `Error: ${error.message}`, type: 'error' });
      }
    },
    async loadTemplates() {
      try {
        const response = await api.doGet('/api/template/custom-actions');
        if (response.status === "00" && response.data) {
          this.templates = response.data;
        } else {
          throw new Error(response.message || "Failed to load templates");
        }
      } catch (error) {
        this.toast.error(error.message || "Failed to load templates");
        this.addConsoleLog({ message: `Error: ${error.message}`, type: 'error' });
      }
    },
    async handleTemplateChange() {
      if (!this.selectedTemplate) return;

      if (this.functionData.code && !confirm('This will replace your current code. Are you sure you want to proceed?')) {
        this.selectedTemplate = '';
        return;
      }

      this.functionData.code = this.selectedTemplate.code;
      this.addConsoleLog({ 
        message: `Template "${this.selectedTemplate.actionName}" loaded`, 
        type: 'info' 
      });
    },
  },
  mounted() {
    this.loadFunction();
    this.loadTemplates();
  }
};
</script>

<style scoped>
.custom-functions-view {
  padding: 20px;
}

.custom-functions-view__header {
  margin-bottom: 30px;
}

.custom-functions-view__editor {
  width: 75%;
  transition: width 0.3s ease;
}

.custom-functions-view__editor--expanded {
  width: calc(100% - 40px);
}

.custom-functions-view__console {
  width: 25%;
  transition: width 0.3s ease;
}

.custom-functions-view__content {
  background: #2b2e4c;
  border-radius: 10px;
  padding: 20px;
}

.nav-tabs {
  border-bottom: 1px solid #424351;
}

.nav-tabs .nav-link {
  color: #fff;
  border: none;
  padding: 10px 20px;
}

.nav-tabs .nav-link.active {
  color: #fff;
  background: transparent;
  border-bottom: 2px solid #007bff;
}

.form-group {
  margin-bottom: 15px;
}

.form-group__label {
  display: block;
  margin-bottom: 5px;
  color: #fff;
}

.form-control {
  background-color: #2b2e4c;
  color: #fff;
  border: 1px solid #424351;
}

.form-control:focus {
  background-color: #2b2e4c;
  color: #fff;
  border-color: #007bff;
  box-shadow: none;
}

textarea.form-control {
  resize: none;
}

.custom-functions-view__templates {
  margin-bottom: 20px;
}

.custom-functions-view__templates select {
  background-color: #2b2e4c;
  color: #fff;
  border: 1px solid #424351;
  padding: 8px;
  border-radius: 4px;
  width: 100%;
}

.custom-functions-view__templates select:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: none;
}

.custom-functions-view__templates select option {
  background-color: #2b2e4c;
  color: #fff;
}
</style> 